/**
 * PaymentOption Component
 * Single payment option dengan radio button
 */

import { Box, Flex, Image, Radio } from '@chakra-ui/react';
import { Text } from '@diamond/shared/ui';
import { UseFormRegister } from 'react-hook-form';

export interface PaymentOptionProps {
  value: string;
  title: string;
  description: string;
  badge?: {
    text: string;
    variant: 'cod' | 'va';
  };
  logoSrc?: string;
  isSelected: boolean;
  onChange: (value: string) => void;
  register: UseFormRegister<any>;
}

/**
 * PaymentOption Component
 * Menampilkan satu opsi pembayaran dengan radio button
 */
export function PaymentOption({
  value,
  title,
  description,
  badge,
  logoSrc,
  isSelected,
  onChange,
}: PaymentOptionProps) {
  // Gradient border sama dengan VoucherSection
  const gradientBorder =
    'linear-gradient(25.45deg, #FF1E1E -6.15%, rgba(36, 81, 206, 0.9) 48.07%, rgba(90, 200, 255, 0.8) 94.92%)';

  // Content yang akan di-render (shared untuk selected dan unselected)
  const content = (
    <Flex align="center" justify="space-between" gap={4} w="full">
      {/* Left: Title + Description */}
      <Box flex={1}>
        <Text
          fontSize="md"
          fontWeight="bold"
          color={isSelected ? 'blue.700' : 'gray.900'}
          mb={1}
        >
          {title}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {description}
        </Text>
      </Box>

      {/* Middle: Badge or Logo */}
      {badge && (
        <Box
          px={3}
          py={1}
          border="1px"
          borderColor="red.500"
          borderRadius="md"
          flexShrink={0}
        >
          <Text
            fontSize="xs"
            fontWeight="bold"
            color="red.500"
            letterSpacing="wide"
          >
            {badge.text}
          </Text>
        </Box>
      )}

      {logoSrc && !badge && (
        <Box flexShrink={0} w="78px" h="auto">
          <Image
            src={logoSrc}
            alt={title}
            objectFit="contain"
            w="full"
            h="auto"
          />
        </Box>
      )}

      {/* Right: Radio Button Circle */}
      <Box position="relative" flexShrink={0}>
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w="24px"
          h="24px"
          borderRadius="full"
          borderWidth="2px"
          borderColor={isSelected ? 'white' : 'gray.300'}
          background={isSelected ? gradientBorder : 'transparent'}
          transition="all 0.2s"
        >
          {isSelected && <Box w="8px" h="8px" borderRadius="full" bg="white" />}
        </Box>
        <Radio
          id={`payment-${value}`}
          value={value}
          isChecked={isSelected}
          onChange={() => onChange(value)}
          position="absolute"
          top={0}
          left={0}
          opacity={0}
          w="24px"
          h="24px"
          cursor="pointer"
          m={0}
        />
      </Box>
    </Flex>
  );

  // Selected state: gradient border wrapper
  if (isSelected) {
    return (
      <Box
        as="label"
        htmlFor={`payment-${value}`}
        cursor="pointer"
        background={gradientBorder}
        borderRadius="md"
        p="2px"
        transition="all 0.2s"
      >
        <Box bg="white" borderRadius="md" p={4}>
          {content}
        </Box>
      </Box>
    );
  }

  // Unselected state: normal border
  return (
    <Box
      as="label"
      htmlFor={`payment-${value}`}
      cursor="pointer"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      p={4}
      bg="white"
      transition="all 0.2s"
      _hover={{
        borderColor: 'gray.300',
        bg: 'gray.50',
      }}
    >
      {content}
    </Box>
  );
}
